/**
 * The client half of the song rights gate (P5.5).
 *
 * The server's projection already omits every field whose capability was not
 * granted. The UI checks AGAIN before rendering anything rights-bearing, so a
 * server defect (or a stale cache) can never put words on screen without a
 * grant: a capability counts only when it is literally `true`.
 */
import type { SongCapability, SongSectionView, SongView } from '../store/api/songLearningApi';

export function granted(view: Pick<SongView, 'capabilities'> | null | undefined, capability: SongCapability): boolean {
  return view?.capabilities?.[capability] === true;
}

/** The words of a section, only with display_lyrics. */
export function visibleLines(view: SongView | null | undefined, section: SongSectionView): string[] | null {
  return granted(view, 'display_lyrics') && Array.isArray(section.lines) && section.lines.length > 0 ? section.lines : null;
}

/** Vocabulary notes quote the words, so they need display_lyrics too. */
export function visibleNotes(view: SongView | null | undefined, section: SongSectionView): string[] | null {
  return granted(view, 'display_lyrics') && Array.isArray(section.notes) && section.notes.length > 0 ? section.notes : null;
}

/** The meaning gloss, only with translate. */
export function visibleGloss(view: SongView | null | undefined, section: SongSectionView): string[] | null {
  return granted(view, 'translate') && Array.isArray(section.gloss) && section.gloss.length > 0 ? section.gloss : null;
}

/** Why a speak-along cannot start for this section, or null when it can. */
export function speakAlongBlocker(view: SongView | null | undefined, section: SongSectionView | null | undefined): string | null {
  if (!view || !section) return 'Choose a section to practise.';
  if (!granted(view, 'session_practice')) return 'Practice is not permitted for this song.';
  if (!visibleLines(view, section)) return 'The words of this section cannot be shown, so it cannot be practised.';
  return null;
}

export type ShareStatus = 'allowed' | 'not_permitted';

/** Sharing goes through existing per-item publication, and only with recording_share. */
export function shareStatus(view: SongView | null | undefined): ShareStatus {
  return granted(view, 'recording_share') ? 'allowed' : 'not_permitted';
}

export const RIGHTS_BASIS_LABEL: Readonly<Record<SongView['rightsBasis'], string>> = Object.freeze({
  original: 'Original BeHeard practice piece',
  public_domain: 'Public domain',
  licensed: 'Licensed',
  none: 'No rights',
});

export const PRONUNCIATION_UNAVAILABLE =
  'Pronunciation and rhythm feedback: not available yet. It needs measured audio from LevelUp Media; BeHeard does not estimate it.';
export const AUDIO_UNAVAILABLE = 'No audio for this piece. Read the words aloud at your own pace.';
export const CHALLENGE_GATED =
  'Song challenges are not available yet. Team challenges do not support a song goal; adding one is a separate, governed change.';
export const SHARE_ALLOWED =
  'Practice recordings are not kept, so a recording cannot be shared. You can share your practice result from Portfolio using per-item sharing.';
export const SHARE_NOT_PERMITTED = 'Sharing is not permitted for this song.';
